<template>
  <PopupInputgroup
    ref="popup"
    class="location-filter"
    :label="placeholder"
    :append-to="appendTo"
    :disabled="!!disabled"
    hide-edit-icon
    @cancel="onCancel"
    @focus="onFocus"
    @save="onSave"
    @keyup.esc="closePopup"
  >
    <template #popupContent>
      <div class="grid formgrid">
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-location-id`"
          :label="t('locations.locations')"
          class="col-6 field"
          optional
        >
          <LocationQuickSearch
            :id="inputId"
            v-model="intermediaryModel"
            v-bind="ariaProps"
            :append-to="appendTo"
            :placeholder="t('locations.locations')"
            @keyup.enter="onEnterInDropdownInput"
            @select="onUpdateNewLocation"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-location-code`"
          :label="t('common.paramCodes', { param: t('locations.locations') })"
          class="col-6 field"
          optional
        >
          <InputText
            :id="inputId"
            v-model="internalValue.locationCode"
            v-bind="ariaProps"
            :placeholder="
              t('common.paramCodes', { param: t('locations.locations') })
            "
            @keyup.enter="onSave"
          />
        </InputWrapper>
      </div>

      <div class="grid formgrid">
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-city`"
          :label="t('address.city')"
          class="col-6 field"
          optional
        >
          <InputText
            :id="inputId"
            v-model="internalValue.city"
            v-bind="ariaProps"
            :placeholder="t('address.city')"
            @keyup.enter="onSave"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-state`"
          :help-text="t('address.stateCode')"
          :label="t('address.stateProvince')"
          class="col-6 field"
          optional
        >
          <InputRegion
            :id="inputId"
            v-model="internalValue.state"
            v-bind="ariaProps"
            :country="internalValue.country"
            @keyup.enter="onSave"
          />
        </InputWrapper>
      </div>

      <div class="grid formgrid">
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-country`"
          :label="t('address.country')"
          class="col-6 field"
          optional
        >
          <CountryDropdown
            :id="inputId"
            v-model="internalValue.country"
            v-bind="ariaProps"
            :append-to="appendTo"
            show-clear
            @keyup.enter="onSave"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-postal-code`"
          :label="t('address.postalCode')"
          class="col-6 field"
          optional
        >
          <InputText
            :id="inputId"
            v-model="internalValue.postalCode"
            v-bind="ariaProps"
            :placeholder="t('address.postalCode')"
            @keyup.enter="onSave"
          />
        </InputWrapper>
      </div>
    </template>

    <template #placeholder="{ label: placeholderLabel }">
      <slot
        name="placeholder"
        :label="placeholderLabel"
        :placeholder="valueText"
      >
        <InputGroup>
          <input
            :id="id"
            class="p-component p-inputtext w-full"
            :placeholder="placeholderLabel"
            :value="valueText"
            @focus="onFocus"
          />

          <InputGroupAddon>
            <RygenButton
              :aria-label="
                t('common.clearParam', {
                  param: t('locations.locationFilters'),
                })
              "
              icon="pi pi-times"
              @click.stop="onClear"
            />
          </InputGroupAddon>
        </InputGroup>
      </slot>
    </template>
  </PopupInputgroup>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { ComponentPublicInstance, PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, useAttrs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';
import { Address, Location } from '@rygen/rygen-ui/model';

import { renderFullAddress } from '@/composables';
import { LocationFilters } from '@/model';

import LocationQuickSearch from '@/components/location/LocationQuickSearch.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CountryDropdown from '@rygen/rygen-ui/components/form/CountryDropdown.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import PopupInputgroup from '@rygen/rygen-ui/components/popup/PopupInputgroup.vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('location-filter').uid,
  },
  modelValue: {
    type: LocationFilters,
    default: () => new LocationFilters(),
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
});
const { id, modelValue, appendTo, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'change', newVal: LocationFilters): void;
  (event: 'update:model-value', newVal: LocationFilters): void;
}>();

defineSlots<{
  placeholder: (props: { label: string; placeholder: string }) => any;
}>();

const attrs = useAttrs();
const { t } = useI18n();

const internalValue: Ref<LocationFilters> = ref(
  new LocationFilters(modelValue.value),
);
const popup: Ref<ComponentPublicInstance | null> = ref(null);
const firstInput: Ref<ComponentPublicInstance | null> = ref(null);

const placeholder = computed(
  () => (attrs.placeholder as string) ?? t('locations.locations'),
);

/**
 * location filter mapped to a `Location` to match model-value of
 * `LocationQuickSearch`
 */
const intermediaryModel = computed({
  get() {
    return new Location({
      name: internalValue.value.locationName,
      code: internalValue.value.locationCode,
      address: new Address({
        locality: internalValue.value.city,
        region: internalValue.value.state,
        postalCode: internalValue.value.postalCode,
        country: internalValue.value.country,
      }),
    });
  },
  set(newVal) {
    internalValue.value.locationId = newVal.id;
    internalValue.value.locationName = newVal.name;
  },
});

const valueText = computed(() => {
  if (internalValue.value.locationCode) {
    return internalValue.value.locationCode;
  }

  if (internalValue.value.locationName) {
    return internalValue.value.locationName;
  }

  const address = new Address({
    locality: internalValue.value.city,
    region: internalValue.value.state,
    postalCode: internalValue.value.postalCode,
  });
  // Address.country defaults to USA, but we don't want that to display if the
  // user hasn't selected a country filter
  address.country = internalValue.value.country;
  return renderFullAddress(address);
});

const onUpdateNewLocation = (newVal: Location) => {
  internalValue.value.locationName = newVal.name;
  internalValue.value.locationId = newVal.id;
};

const onEnterInDropdownInput = (event: KeyboardEvent) => {
  // Stop the keyboard event from bubbling up to the popup card so that the
  // popup isn't closed.
  event.stopPropagation();
};

const onFocus = () => {
  if (disabled.value) {
    return;
  }

  nextTick(() => firstInput.value?.$el?.focus());
};

const onSave = async () => {
  fireEvents();
};

const onCancel = () => {
  internalValue.value = new LocationFilters(modelValue.value);
};

const closePopup = () => {
  if (!popup.value) {
    return;
  }

  const popupRef = popup.value as unknown as typeof PopupInputgroup;
  popupRef.onCancel();
};

const onClear = () => {
  internalValue.value = new LocationFilters();
  fireEvents();
};

const fireEvents = () => {
  if (!isEqual(internalValue.value, modelValue.value)) {
    emit('update:model-value', internalValue.value);
    emit('change', internalValue.value);
  }
};

watch(
  modelValue,
  (newVal: LocationFilters) => {
    internalValue.value = new LocationFilters(newVal);
  },
  { deep: true, immediate: true },
);
</script>
